<template lang="pug">
div.px-8
  v-row(no-gutters)
    v-col.pr-8.shrink
      div.sticky.white(style="width: 230px; top: 106px;")
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/registration-settings')") Registration settings
        .text-md.font-semibold.neutral200.px-6.py-2.round-md.mb-2 Consultation settings
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/checkout-settings')") Checkout settings

    v-col.grow
      v-container.pt-10
        v-breadcrumbs.pt-0.px-0.pb-3(:items="breadcrumbItems")
          template(v-slot:item="{ item }")
            a.text-sm.font-medium.no-underline.neutral700--text(v-if="!item.disabled" @click="$router.push(item.href)") {{ item.text }}
            .text-sm.neutral700--text(v-else) {{ item.text }}

        .text-2xl.font-semibold.neutral700--text Consultation settings
        v-divider.mt-2
        .text-lg.font-medium.neutral700--text.mt-4 Diagnosis list
        .text-sm.neutral700--text Add, edit, and remove your diagnosis.

        v-row.mt-6.pb-2.pb-md-0(no-gutters)
          v-col(cols="12" md="7")
            v-tabs(v-model="tabs" color="basic700" :height="44" :grow="isMobileScreen")
              v-tab.text-sm.font-semibold.text-none.px-4 {{ $t('all') }}
              v-tab.text-sm.font-semibold.text-none.px-4 Active
              v-tab.text-sm.font-semibold.text-none.px-4 Archived
          v-col(cols="12" md="5" align="end")
            .d-flex.align-center
              v-spacer
              v-btn(@click="clickSearch" icon color="neutral500")
                v-icon(size="20") $yzi-search-sm
              v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
              v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="addNewDiagnoses") Add diagnosis
        v-divider

        .data-table
          v-data-table.mt-sm-4.pb-6(:headers="headers" :items="consultations" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }")
            //- name
            template(v-slot:item.name="{ item }")
              .text-sm.neutral700--text {{ item.value }}
            //- status
            template(v-slot:item.status="{ item }")
              div
                y-switch.mb-n3.ml-n3(v-model="item.enabled" @change="updateStatus(item, item.enabled)")
            //- actions
            template(v-slot:item.actions="{ item, index }")
              div.d-flex
                v-spacer
                v-menu(offset-y :top="menuPositionTop(index)" left attach rounded="lg")
                  template(#activator="{ on }")
                    v-btn.mr-3(icon small v-on="on")
                      v-icon mdi-dots-horizontal
                  v-list.py-0(width="100")
                    v-list-item(@click="editItem(item)")
                      v-list-item-title.text-sm.neutral700--text Edit
                    v-divider
                    v-list-item(@click="deleteItem(item)")
                      v-list-item-title.text-sm.danger500--text Delete

        div.d-flex.align-center(v-if="totalCount > pageSize")
          v-spacer
          yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

        .dialogs
          add-diagnosis-dialog(v-if="openAddDiagnosesDialog" :open.sync="openAddDiagnosesDialog" :parentDiagnosis="selectedDiagnosis" @closed="closed" @reset="reset")
          delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="closed" @delete="confirmDelete")
            .text-lg.font-medium Are you sure you want to delete this diagnosis?
            .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed

</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import AddDiagnosisDialog from '@/components/clinic/settings/AddDiagnosisDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  components: { AddDiagnosisDialog, DeleteConfirmationDialog },
  data: () => ({
    showSearchField: false,
    isFetching: false,
    isLoading: false,
    openAddDiagnosesDialog: false,
    openDeleteDialog: false,
    selectedDiagnosis: null,
    tabs: 0,
    pageSize: 20,
    searchText: '',
    totalCount: 0,
    currentPage: 1,
    consultations: [],
    headers: [
      { text: 'Name', value: 'name', enable: true },
      { text: 'Status', value: 'status', enable: true, width: '100' },
      { text: 'Actions', value: 'actions', enable: true, class: 'text-end', width: '100' },
    ],
    breadcrumbItems: [
      {
        text: 'Clinic management',
        disabled: false,
        href: '/admin/clinic/settings'
      },
      {
        text: 'Consultation settings',
        disabled: true
      }
    ]
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
  },
  watch: {
    tabs () {
      this.searchText = null
      this.currentPage = 1
      this.fetchConsultationList()
    },
    pageSize () {
      this.currentPage = 1
      this.fetchConsultationList()
    }
  },
  mounted () {
    this.fetchConsultationList()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchConsultationList () {
      this.isFetching = true
      let params = {
        page_size: this.pageSize,
        page: this.currentPage,
        company: this.company.id,
        field: 'consultation.diagnosis'
      }
      if (this.tabs === 1) { params.enabled = true }
      if (this.tabs === 2) { params.enabled = false }
      if (this.searchText) { params.value__contains = this.searchText }
      this.$axios.get(`/options/`, { params })
        .then(resp => {
          this.consultations = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    updateStatus (item, status) {
      this.isFetching = true
      this.$axios.patch(`/options/${item.id}`, { enabled: status })
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isFetching = false
        })
    },
    confirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/options/${this.selectedDiagnosis.id}`)
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchConsultationList()
    }, 750),
    pageChanged () {
      this.fetchConsultationList()
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    addNewDiagnoses () {
      this.openAddDiagnosesDialog = true
    },
    editItem (item) {
      this.selectedDiagnosis = item
      this.openAddDiagnosesDialog = true
    },
    deleteItem (item) {
      this.selectedDiagnosis = item
      this.openDeleteDialog = true
    },
    reset () {
      this.currentPage = 1
      this.fetchConsultationList()
      this.selectedDiagnosis = null
      this.openDeleteDialog = false
      this.openAddDiagnosesDialog = false
    },
    closed () {
      this.selectedDiagnosis = null
      this.openDeleteDialog = false
      this.openAddDiagnosesDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 7) {
        let start = this.consultations.length - 2
        if (position > start) {
          return true
        }
      }
      return false
    },
  }
}
</script>

<style lang="scss" scoped>
</style>